import type { QueryClient } from '@tanstack/react-query';
import type { BusinessDoc } from './converters.js';
import { keys } from './keys.js';

/**
 * Apply a just-saved change to the cached business list, without waiting for
 * the Firestore listener to echo it back.
 *
 * ## Why this is needed rather than nice
 *
 * The setup wizard decides which step you may be on by recomputing
 * `onboardingProgress` from live data, and clamps the URL to the first
 * outstanding step. A step that saves and then advances is therefore racing its
 * own listener: the mutation resolves, the step calls `setParams({ step: next })`,
 * and the clamp — still reading the pre-save snapshot — pulls the owner
 * straight back to the step they just completed. From the outside it looks like
 * the button did nothing.
 *
 * Writing the saved value into the cache closes that gap. It is not a guess:
 * these are the exact fields we just persisted, and the authoritative snapshot
 * overwrites them milliseconds later.
 *
 * Only FLAT fields are merged. `BusinessSettingsPatch` carries nested blocks
 * (`registration`, `sms`, `email`, `facts`) that the adapter flattens into dotted field
 * paths, so a naive spread here would replace a whole sub-document with the
 * partial one being written and briefly show an owner a business that had lost
 * half its settings. Those blocks simply wait for the snapshot — nothing in the
 * wizard's progress depends on them.
 */
/**
 * Apply a just-declared business line to the cached business (doc 18 D2).
 *
 * Its own function rather than a call to {@link patchCachedBusiness}, because
 * the value does not live in a flat field: once a forwarding target exists the
 * declaration is stored INSIDE the `forwardNumber` map, which that helper drops
 * on purpose — a blind spread there would replace a whole sub-document with a
 * partial one. The merge here is explicit and keeps every other key of the map.
 *
 * Why this is needed rather than nice. The card that prints the number reads it
 * from this cache, and nothing else writes it: `declareSourceNumber` is a
 * callable, so the new number reaches the screen only when the Firestore
 * listener echoes the write back. Until it does, an owner who has just pressed
 * Save is looking at their OLD number under a green "forwarding live" badge —
 * the exact state the change was meant to leave behind — and on a connection
 * that is slow, throttled or briefly asleep that is long enough to read as
 * "the button did nothing". The authoritative snapshot overwrites this moments
 * later; these are the values the server just told us it stored.
 */
export function patchCachedSourceNumber(
  qc: QueryClient,
  uid: string,
  businessId: string,
  /** Canonical E.164, as returned by the callable — never what was typed. */
  sourceNumber: string,
  /** The server dropped the old proof because the line really changed. */
  reverificationRequired: boolean,
): void {
  qc.setQueryData<BusinessDoc[]>(keys.myBusinesses(uid), (list) =>
    list?.map((b) => {
      if (b.id !== businessId) return b;
      // No forwarding target yet: the declaration waits in its own flat field
      // until there is a `forwardNumber` map to hold it. The same split the
      // callable makes, for the same reason — a map with only `sourceNumber` in
      // it lacks `phoneNumber`/`isoCountry` and parses nowhere.
      if (!b.forwardNumber) return { ...b, pendingSourceNumber: sourceNumber };
      return {
        ...b,
        forwardNumber: {
          ...b.forwardNumber,
          sourceNumber,
          // Mirrors `declareSourceNumber`'s own reset, field for field. Leaving
          // the proof standing would print the NEW number under a "live" badge
          // and hide the check that the new line has not yet passed — a worse
          // lie than the stale number this patch exists to remove.
          ...(reverificationRequired
            ? {
                sourceVerifiedAt: null,
                poolingUnsupportedAt: null,
                sourceMismatchAt: null,
                observedSourceNumber: null,
                verifiedAt: null,
                testExpiresAt: null,
              }
            : {}),
        },
      };
    }),
  );
}

export function patchCachedBusiness(
  qc: QueryClient,
  uid: string,
  businessId: string,
  patch: Partial<BusinessDoc>,
): void {
  const flat = Object.fromEntries(
    Object.entries(patch).filter(([, v]) => v === null || typeof v !== 'object' || v instanceof Date),
  ) as Partial<BusinessDoc>;
  if (Object.keys(flat).length === 0) return;

  qc.setQueryData<BusinessDoc[]>(keys.myBusinesses(uid), (list) =>
    list?.map((b) => (b.id === businessId ? { ...b, ...flat } : b)),
  );
}

/**
 * Put a business read straight from the server into the cached list — the
 * fallback for a live listener that has gone quiet (see `serverRead.ts`).
 * Returns whether it did, which is also the news that the listener is behind.
 *
 * Only a NEWER copy replaces the cached one: a poll that left before the
 * listener delivered a later write must not land after it and roll the screen
 * back, and an equally fresh copy is left alone so a healthy listener plus a
 * poll every few seconds does not re-render the page for nothing.
 *
 * Newer by either of two stamps. `updatedAt` covers every write, but a cached
 * one can be the SDK's local estimate of a pending write (the converter reads
 * `serverTimestamps: 'estimate'`), and on a device whose clock runs fast that
 * estimate outranks the server's real, later stamp. `forwardNumber.verifiedAt`
 * is written only by the gateway, never estimated locally, so a check that has
 * concluded on the server always gets through on it.
 */
export function replaceCachedBusiness(qc: QueryClient, uid: string, fresh: BusinessDoc): boolean {
  const ms = (d: unknown) => (d instanceof Date ? d.getTime() : 0);
  const newer = (b: BusinessDoc) =>
    ms(fresh.updatedAt) > ms(b.updatedAt) ||
    ms(fresh.forwardNumber?.verifiedAt) > ms(b.forwardNumber?.verifiedAt);
  let replaced = false;
  qc.setQueryData<BusinessDoc[]>(keys.myBusinesses(uid), (list) =>
    list?.map((b) => {
      if (b.id !== fresh.id || !newer(b)) return b;
      replaced = true;
      return fresh;
    }),
  );
  return replaced;
}
